// src/components/AddRevenueScreen.jsx
// Novo/editar receita com validação, normalização de dados e UI premium responsiva.
// Rotas sugeridas:
// - /finance/revenue/new
// - /finance/revenue/edit  state { revenue } ou state { args }
// Ao salvar: volta para /finance com state { refresh: true }

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  CircleDollarSign,
  LoaderCircle,
  Menu,
  Mic2,
  NotebookTabs,
  Puzzle,
  Save,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/addRevenue.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const CATEGORIES = [
  {
    value: "Consulta",
    label: "Consulta",
    icon: CircleDollarSign,
    description: "Recebimento por atendimento individual.",
  },
  {
    value: "Palestra",
    label: "Palestra",
    icon: Mic2,
    description: "Receita por eventos e apresentações.",
  },
  {
    value: "Workshop",
    label: "Workshop",
    icon: Puzzle,
    description: "Receita por treinamentos e grupos.",
  },
  {
    value: "Outro",
    label: "Outro",
    icon: Sparkles,
    description: "Outras entradas financeiras.",
  },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function pad2(value) {
  return String(value).padStart(2, "0");
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function normalizeSpaces(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function todayISO() {
  const date = new Date();

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
}

function dateToISODate(dateLike) {
  if (!dateLike) return "";

  const raw = String(dateLike).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) return "";

  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
    parsed.getDate()
  )}`;
}

function isValidISODate(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function isFutureISODate(value) {
  if (!isValidISODate(value)) return false;

  const selected = new Date(`${value}T00:00:00`);
  const today = new Date(`${todayISO()}T00:00:00`);

  return selected > today;
}

function formatDateBR(value) {
  if (!value) return "Não informado";

  const iso = dateToISODate(value);
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return "Não informado";

  return `${match[3]}/${match[2]}/${match[1]}`;
}

function parseMoneyBR(value) {
  if (value === null || value === undefined || value === "") return NaN;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  const normalized = String(value)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatMoneyFromDigits(value) {
  const digits = onlyDigits(value).slice(0, 10);

  if (!digits) return "";

  const number = Number(digits) / 100;

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoneyForInput(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "";

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrencyBR(value) {
  const number = parseMoneyBR(value);

  if (!Number.isFinite(number)) return "R$ 0,00";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function getCategoryInfo(category) {
  return CATEGORIES.find((item) => item.value === category) || CATEGORIES[0];
}

function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <div className={`rev-field ${className} ${error ? "rev-field--error" : ""}`}>
      <div className="rev-field__top">
        <label className="rev-label">
          {label}
          {required ? <span className="rev-req">*</span> : null}
        </label>

        {hint ? <span className="rev-hint">{hint}</span> : null}
      </div>

      {children}

      {error ? (
        <div className="rev-err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function CategoryCard({
  option,
  active,
  disabled,
  onSelect,
}) {
  const Icon = option.icon;

  return (
    <button
      className={`rev-category-card ${
        active
          ? "rev-category-card--active"
          : ""
      }`}
      type="button"
      onClick={() =>
        onSelect(option.value)
      }
      disabled={disabled}
      aria-pressed={active}
    >
      <span className="rev-category-card__icon">
        <Icon
          size={20}
          aria-hidden="true"
        />
      </span>

      <span className="rev-category-card__content">
        <strong>{option.label}</strong>
        <small>{option.description}</small>
      </span>
    </button>
  );
}

export default function AddRevenueScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const pageRef = useRef(null);

  const revenue = location.state?.revenue || location.state?.args || null;
  const revenueId = revenue?._id || null;
  const isEdit = Boolean(revenueId);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [valor, setValor] = useState("");
  const [descricao, setDescricao] = useState("");
  const [dataISO, setDataISO] = useState("");
  const [categoria, setCategoria] = useState("Consulta");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const selectedCategory = useMemo(
    () => getCategoryInfo(categoria),
    [categoria]
  );

  const SelectedCategoryIcon = selectedCategory.icon;

  const completion = useMemo(() => {
    const checks = [
      Number.isFinite(parseMoneyBR(valor)) && parseMoneyBR(valor) > 0,
      Boolean(categoria),
      Boolean(dataISO) && isValidISODate(dataISO),
    ];

    if (descricao) {
      checks.push(normalizeSpaces(descricao).length >= 3);
    }

    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [valor, categoria, dataISO, descricao]);

  const title = isEdit ? "Editar receita" : "Adicionar receita";

  useEffect(() => {
    if (!isEdit) {
      setDataISO(todayISO());
      return;
    }

    setValor(formatMoneyForInput(revenue?.valor ?? ""));
    setDescricao(revenue?.descricao ?? "");
    setCategoria(revenue?.categoria ?? "Consulta");
    setDataISO(dateToISODate(revenue?.data) || todayISO());
  }, [isEdit, revenue]);

  const showMessage = useCallback(
    (type, text, options = {}) => {
      const tone = [
        "success",
        "warning",
        "error",
        "info",
      ].includes(type)
        ? type
        : "info";

      const defaultTitles = {
        success: "Receita registrada",
        warning: "Revise as informações",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      toast[tone]({
        title: options.title || defaultTitles[tone],
        message: text || "",
        details: Array.isArray(options.details)
          ? options.details
          : [],
        actionLabel: options.actionLabel || "",
        onAction:
          typeof options.onAction === "function"
            ? options.onAction
            : null,
        duration:
          options.duration !== undefined
            ? options.duration
            : options.actionLabel
              ? 0
              : 5200,
      });
    },
    [toast]
  );

  const focusFirstError = () => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(".rev-err");
      const target =
        errorElement?.closest("label, .app-field, .pr-field, .rev-field, .al-form-card, .al-panel, section") ||
        errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

  const authOrRedirect = () => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showMessage(
        "error",
        "Sessão expirada. Faça login novamente.",
        {
          title: "Sessão encerrada",
          duration: 6500,
        }
      );

      navigate(
        "/login",
        {
          replace: true,
        }
      );
      return null;
    }

    return { token, userId };
  };

  const setValorSafe = (value) => {
    setValor(formatMoneyFromDigits(value));
    setErrors((prev) => ({ ...prev, valor: "" }));
  };

  const setDataSafe = (value) => {
    setDataISO(value);
    setErrors((prev) => ({ ...prev, data: "" }));
  };

  const setCategoriaSafe = (value) => {
    setCategoria(value);
    setErrors((prev) => ({ ...prev, categoria: "" }));
  };

  const setDescricaoSafe = (value) => {
    setDescricao(value);
    setErrors((prev) => ({ ...prev, descricao: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    const valorNumber = parseMoneyBR(valor);

    if (!String(valor || "").trim()) {
      nextErrors.valor = "Informe o valor da receita.";
    } else if (!Number.isFinite(valorNumber) || valorNumber <= 0) {
      nextErrors.valor = "Informe um valor maior que zero.";
    }

    if (!CATEGORIES.some((item) => item.value === categoria)) {
      nextErrors.categoria = "Selecione uma categoria válida.";
    }

    if (!dataISO) {
      nextErrors.data = "Informe a data da receita.";
    } else if (!isValidISODate(dataISO)) {
      nextErrors.data = "Data inválida.";
    } else if (isFutureISODate(dataISO)) {
      nextErrors.data = "A data da receita não pode ser futura.";
    }

    if (descricao && normalizeSpaces(descricao).length < 3) {
      nextErrors.descricao = "A descrição precisa ter pelo menos 3 caracteres.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage("error", "Revise os campos destacados antes de salvar.");
      focusFirstError();
      return false;
    }

    return true;
  };

  const saveReceita = async (payload) => {
    const auth = authOrRedirect();
    if (!auth) return false;

    const url = revenueId
      ? `${API_BASE}/psy/receita/${encodeURIComponent(
          revenueId
        )}?id=${encodeURIComponent(auth.userId)}`
      : `${API_BASE}/psy/receita?id=${encodeURIComponent(auth.userId)}`;

    const response = await fetch(url, {
      method: revenueId ? "PUT" : "POST",
      headers: {
        Authorization: `Bearer ${auth.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    saveAuthToken(response.headers.get("x-new-token"));

    if (response.status === 401 || response.status === 403) {
      showMessage(
        "error",
        "Sessão expirada. Faça login novamente.",
        {
          title: "Sessão encerrada",
          duration: 6500,
        }
      );

      navigate(
        "/login",
        {
          replace: true,
        }
      );
      return false;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      showMessage(
        "error",
        data?.message || `Erro ao salvar receita (${response.status}).`
      );
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      const payload = {
        valor: parseMoneyBR(valor),
        categoria,
        data: dataISO,
        descricao: normalizeMultiline(descricao),
      };

      const ok = await saveReceita(payload);
      if (!ok) return;

      showMessage(
        "success",
        isEdit
          ? "Receita atualizada com sucesso."
          : "Receita adicionada com sucesso.",
        {
          title: isEdit
            ? "Receita atualizada"
            : "Receita adicionada",
        }
      );

      navigate(
        "/finance",
        {
          replace: true,
          state: {
            refresh: true,
          },
        }
      );
    } catch (error) {
      console.error("Erro ao salvar receita:", error);
      showMessage("error", "Falha de comunicação com a API.");
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (saving) return;
    navigate("/finance");
  };

  return (
    <div className="rev-page" ref={pageRef}>
      <div className="rev-bg" aria-hidden="true">
        <span className="rev-bg__orb rev-bg__orb--one" />
        <span className="rev-bg__orb rev-bg__orb--two" />
        <span className="rev-bg__orb rev-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="rev-topbar">
        <button
          className="rev-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="rev-brand">
          <img src={Logo} alt="PsyAssist" className="rev-brand__logo" />
          <div className="rev-brand__text">
            <strong>{title}</strong>
            <span>Financeiro • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="rev-top-actions">
          <button
            className="rev-btn rev-btn--ghost rev-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Voltar
          </button>

          <button
            className="rev-btn rev-btn--primary"
            type="button"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="rev-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} aria-hidden="true" />
                {isEdit ? "Atualizar" : "Salvar"}
              </>
            )}
          </button>
        </div>
      </header>

      <main className="rev-main">
        <section className="rev-hero">
          <div>
            <span className="rev-kicker">
              <TrendingUp size={15} aria-hidden="true" />
              {isEdit ? "Atualização financeira" : "Nova entrada"}
            </span>
            <h1>{title}</h1>
            <p>
              Registre entradas financeiras com valor padronizado, categoria,
              data validada e descrição opcional para facilitar seus relatórios.
            </p>
          </div>

          <aside className="rev-preview-card">
            <div className="rev-preview-card__icon" aria-hidden="true">
              <SelectedCategoryIcon size={24} />
            </div>
            <div>
              <small>Prévia da receita</small>
              <strong>{formatCurrencyBR(valor)}</strong>
              <span>
                {selectedCategory.label} • {formatDateBR(dataISO)}
              </span>
            </div>
          </aside>
        </section>

        <section className="rev-wrap">
          <div className="rev-workspace">
            <section className="rev-card" aria-busy={saving}>
            <div className="rev-card__head">
              <div>
                <span className="rev-card__eyebrow">
                  <NotebookTabs size={14} aria-hidden="true" />
                  Receita
                </span>
                <h2>Dados do lançamento</h2>
                <p>
                  Campos obrigatórios são validados antes do envio. A data não
                  pode ser futura para manter o caixa consistente.
                </p>
              </div>

              <div className="rev-score">
                <small>
                  <CheckCircle2 size={13} aria-hidden="true" />
                  Completude
                </small>
                <strong>{completion}%</strong>
                <div className="rev-score__bar">
                  <span style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>

            <div className="rev-grid">
              <Field label="Valor" required error={errors.valor} hint="R$">
                <input
                  className="rev-input"
                  value={valor}
                  onChange={(event) => setValorSafe(event.target.value)}
                  placeholder="150,00"
                  inputMode="numeric"
                />
              </Field>

              <Field label="Data" required error={errors.data}>
                <input
                  className="rev-input"
                  type="date"
                  value={dataISO}
                  onChange={(event) => setDataSafe(event.target.value)}
                  max={todayISO()}
                />
              </Field>

              <Field
                label="Categoria"
                required
                error={errors.categoria}
                className="rev-field--full"
              >
                <div className="rev-category-grid">
                  {CATEGORIES.map((option) => (
                    <CategoryCard
                      key={option.value}
                      option={option}
                      active={categoria === option.value}
                      disabled={saving}
                      onSelect={setCategoriaSafe}
                    />
                  ))}
                </div>
              </Field>

              <Field
                label="Descrição"
                error={errors.descricao}
                hint="Opcional"
                className="rev-field--full"
              >
                <textarea
                  className="rev-textarea"
                  value={descricao}
                  onChange={(event) => setDescricaoSafe(event.target.value)}
                  onBlur={(event) =>
                    setDescricaoSafe(normalizeMultiline(event.target.value))
                  }
                  placeholder="Ex.: Pagamento de sessão, pacote mensal, palestra, workshop..."
                  rows={4}
                />
              </Field>

              <div className="rev-summary">
                <article>
                  <span>Valor informado</span>
                  <strong>{formatCurrencyBR(valor)}</strong>
                </article>

                <article>
                  <span>Categoria</span>
                  <strong>{selectedCategory.label}</strong>
                </article>

                <article>
                  <span>Data</span>
                  <strong>{formatDateBR(dataISO)}</strong>
                </article>
              </div>

              <div className="rev-actions" aria-label="Ações da receita">
                <button
                  className="rev-btn rev-btn--ghost"
                  type="button"
                  onClick={goBack}
                  disabled={saving}
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  Voltar
                </button>

                <button
                  className="rev-btn rev-btn--primary"
                  type="button"
                  onClick={onSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <LoaderCircle
                        size={17}
                        aria-hidden="true"
                        className="rev-icon-spin"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save size={17} aria-hidden="true" />
                      {isEdit ? "Atualizar receita" : "Salvar receita"}
                    </>
                  )}
                </button>
              </div>
            </div>
            </section>

            <aside className="rev-context">
              <section className="rev-context-card rev-context-card--summary">
                <div className="rev-context-card__head">
                  <CircleDollarSign size={19} aria-hidden="true" />
                  <div>
                    <span>Valor da receita</span>
                    <strong>{formatCurrencyBR(valor)}</strong>
                  </div>
                </div>

                <dl>
                  <div>
                    <dt>Categoria</dt>
                    <dd>{selectedCategory.label}</dd>
                  </div>
                  <div>
                    <dt>Data</dt>
                    <dd>{formatDateBR(dataISO)}</dd>
                  </div>
                  <div>
                    <dt>Modo</dt>
                    <dd>{isEdit ? "Edição" : "Novo lançamento"}</dd>
                  </div>
                  <div>
                    <dt>Completude</dt>
                    <dd>{completion}%</dd>
                  </div>
                </dl>
              </section>

              <section className="rev-context-card">
                <div className="rev-context-card__title">
                  <ShieldCheck size={18} aria-hidden="true" />
                  <strong>Consistência financeira</strong>
                </div>

                <ul>
                  <li>A data não pode ser futura.</li>
                  <li>O valor deve ser maior que zero.</li>
                  <li>A descrição é opcional, mas melhora os relatórios.</li>
                </ul>
              </section>

              <section className="rev-context-card rev-context-card--category">
                <div className="rev-context-card__category-icon" aria-hidden="true">
                  <SelectedCategoryIcon size={20} />
                </div>
                <div>
                  <span>Categoria selecionada</span>
                  <strong>{selectedCategory.label}</strong>
                  <p>{selectedCategory.description}</p>
                </div>
              </section>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}
